import { z } from 'zod';
import { uuidSchema } from '../common';
import { LINKED_EXPIRY_REFUSAL, credentialExpiresAtSchema, type CredentialExpiryResponse } from './credential-expiry';

export const connectionIdParam = z.object({ id: uuidSchema });
export const connectorIdParam = z.object({ id: uuidSchema });

export const listConnectionsQuery = z.object({ hub_id: uuidSchema });

export const deleteConnectionQuery = z.object({ hub_id: uuidSchema });

export const addConnectionBody = z.object({
  hub_id: uuidSchema,
  connector_id: uuidSchema,
  authentication_type: z.string().optional(),
  inbound_mode: z.enum(['wayai', 'resend']).optional(),
  inbound_address: z.string().email().optional(),
  // Opt out of copying this connection's credentials into production on publish/sync.
  // Defaults to true at the DB layer.
  sync_credentials_to_production: z.boolean().optional(),
  // When the connection's own secret expires; null for none (`credential-expiry.ts`).
  expires_at: credentialExpiresAtSchema.optional(),
}).passthrough().superRefine((data, ctx) => {
  if (data.inbound_mode === 'resend' && !data.inbound_address) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Receiving email address is required when using Resend inbound mode',
      path: ['inbound_address'],
    });
  }
});

export const editConnectionBody = z.object({
  hub_id: uuidSchema,
  sync_credentials_to_production: z.boolean().optional(),
  expires_at: credentialExpiresAtSchema.optional(),
}).passthrough();

/**
 * PATCH /connections/:id/production-credential — sets a credential directly on a
 * PRODUCTION connection (the sanctioned carve-out from production read-only, used when
 * `sync_credentials_to_production` is off). `.strict()` so only credential-source fields
 * can be written — never arbitrary connection config on a production hub. Exactly one
 * source: either an org-credential link (`organization_credential_id`, optionally
 * `organization_credential_token_id`) OR direct raw secret field(s) — save a hosted agent
 * connection's `webhook_secret_token` and GitHub App (`github_app_id`, `github_app_private_key`),
 * which no org credential supplies, so they may ride beside a link as well as alone. The secret's
 * `expires_at` rides beside direct fields, or alone to change the date of the credential already
 * stored; never beside a link, whose org credential holds its own.
 */
const PRODUCTION_DIRECT_FIELDS = ['username', 'api_key', 'access_token', 'password', 'refresh_token', 'webhook_secret_token', 'webhook_signing_secret', 'github_app_id', 'github_app_private_key'] as const;
/** The hosted agent connection's credentials no org credential supplies (wayai-pro/wayai#5083 Phase 4). */
const BESIDE_LINK_FIELDS: ReadonlySet<string> = new Set(['webhook_secret_token', 'github_app_id', 'github_app_private_key']);
export const setProductionCredentialBody = z.object({
  hub_id: uuidSchema,
  organization_credential_id: z.string().nullable().optional(),
  organization_credential_token_id: z.string().nullable().optional(),
  username: z.string().optional(),
  api_key: z.string().optional(),
  access_token: z.string().optional(),
  password: z.string().optional(),
  refresh_token: z.string().optional(),
  webhook_secret_token: z.string().optional(),
  webhook_signing_secret: z.string().optional(),
  github_app_id: z.string().optional(),
  github_app_private_key: z.string().optional(),
  expires_at: credentialExpiresAtSchema.optional(),
}).strict().superRefine((data, ctx) => {
  const hasOrgLink = !!data.organization_credential_id || !!data.organization_credential_token_id;
  // A masked field the console left untouched arrives as its keep sentinel (`__USE_EXISTING_<FIELD>__`),
  // which writes nothing: it is neither a direct secret beside a link nor a credential of its own.
  const sets = (f: (typeof PRODUCTION_DIRECT_FIELDS)[number]) =>
    data[f] != null && data[f] !== `__USE_EXISTING_${f.toUpperCase()}__`;
  const hasDirect = PRODUCTION_DIRECT_FIELDS.some(sets);
  // A hosted agent connection's webhook signing secret and GitHub App (wayai-pro/wayai#5083 Phase 4)
  // are no org credential's to supply, so they ride beside an org link as well as alone.
  const hasDirectBesideLink = PRODUCTION_DIRECT_FIELDS.some((f) => !BESIDE_LINK_FIELDS.has(f) && sets(f));
  if (hasOrgLink && hasDirectBesideLink) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Provide either an organization credential link or direct credential fields, not both' });
  }
  if (hasOrgLink && data.expires_at != null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: LINKED_EXPIRY_REFUSAL, path: ['expires_at'] });
  }
  if (!hasOrgLink && !hasDirect && data.expires_at === undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'A credential is required: link an organization credential or provide a credential field' });
  }
});

export const registerWhatsappQuery = z.object({ hub_id: uuidSchema });

export const registerWhatsappBody = z.object({
  pin: z.string().optional(),
});

// =============================================================================
// RESPONSE TYPES
// =============================================================================

/** Single connection item as returned by the `get_connections` RPC. */
export interface ConnectionItemResponse extends CredentialExpiryResponse {
  connection_id: string | null;
  hub_id: string;
  account_sid: string | null;
  connection_display_name: string | null;
  username: string | null;
  state: Record<string, unknown> | null;
  settings: Record<string, unknown> | null;
  organization_credential_id: string | null;
  organization_credential_token_id: string | null;
  connector_id: string;
  connector_name: string;
  connector_type: string;
  service_name: string;
  authentication_type: string;
  authentication_types: string[];
  unique_per_hub: boolean;
  account_sid_placeholder: string | null;
  init_endpoint: string | null;
  connector_credential_schema: Record<string, unknown> | null;
  connector_settings_schema: Record<string, unknown> | null;
  agent_settings_schema: Record<string, unknown> | null;
  available_action: 'Add' | 'Edit' | 'Remove' | 'Enable' | 'Disable';
  enabled: boolean;
  sync_credentials_to_production: boolean;
  base_url: string | null;
  connection_headers: Record<string, unknown> | null;
}

/** GET /connections?hub_id=... */
export interface ListConnectionsResponse {
  connections: ConnectionItemResponse[];
}

/** POST /connections (add connection — may return OAuth redirect) */
export interface AddConnectionResponse {
  connections: ConnectionItemResponse[];
  warnings?: string[];
  oauth_url?: string;
  state?: string;
}

/** PATCH /connections/:id (edit connection) */
export interface EditConnectionResponse {
  connections: ConnectionItemResponse[];
  warnings?: string[];
}

/** PATCH /connections/:id/production-credential (set production connection credential) */
export interface SetProductionCredentialResponse {
  connections: ConnectionItemResponse[];
}

/** POST /connections/:id/enable */
export interface EnableConnectionResponse {
  connections: ConnectionItemResponse[];
  warnings?: string[];
}

/** POST /connections/:id/disable */
export interface DisableConnectionResponse {
  connections: ConnectionItemResponse[];
}

/** DELETE /connections/:id */
export interface DeleteConnectionResponse {
  connections: ConnectionItemResponse[];
}

/** GET /connections/:id/channel-info */
export interface ChannelInfoResponse {
  channel: Record<string, unknown> | null;
  connection: Record<string, unknown>;
}

/** POST /connections/:id/sync-mcp */
export interface SyncMcpConnectionResponse {
  success: boolean;
  tools_count?: number;
  resources_count?: number;
  message?: string;
  warnings?: string[];
}

/** POST /connections/:id/register-whatsapp */
export interface RegisterWhatsappResponse {
  success: boolean;
  message?: string;
  error?: string;
}

// =============================================================================
// ASYNC PROVISIONING RETRY
// =============================================================================

/** POST /connections/:id/retry-provisioning — query */
export const retryProvisioningQuery = z.object({ hub_id: uuidSchema });

/** POST /connections/:id/retry-provisioning — response */
export interface RetryProvisioningResponse {
  success: true;
  connection_id: string;
}

// =============================================================================
// RESEND DOMAIN VERIFICATION
// =============================================================================

export const resendDomainStatusQuery = z.object({ hub_id: uuidSchema });

export interface ResendDnsRecord {
  record: string;
  name: string;
  type: string;
  ttl: string;
  status: string;
  value: string;
  priority?: number;
}

/** GET /connections/:id/domain-status */
export interface ResendDomainStatusResponse {
  verified: boolean;
  domain_id: string | null;
  domain_name: string | null;
  status: string;
  records: ResendDnsRecord[];
  inbound_address: string | null;
  inbound_mode: 'wayai' | 'resend' | null;
}

/** POST /connections/:id/verify-domain */
export interface ResendVerifyDomainResponse {
  domain_id: string;
  status: string;
}
